/**
 * Pure helpers for the Studio tool detail page (`tools.$toolId.tsx`).
 *
 * Extracted so the Test tab's policy → enable/disable matrix can be
 * unit-tested without RTL/JSDOM. The route file still owns the React
 * state + dialog wiring; this module just provides the input-only
 * decision functions.
 *
 * #258 verification — the prior bug was that `require_approval`
 * disabled the Run Test button entirely. The current route file
 * (post #1093) enables it AND adds a confirmation dialog. This
 * helper pins that matrix so a future "drift fix" cannot silently
 * regress to the disabled state.
 */

export type ToolPolicy = 'auto_approve' | 'require_approval' | 'forbidden';

/**
 * Decide whether the "Run Test" button should be ENABLED for the
 * given tool state. Caller still passes the runtime checks
 * (`testTool.isPending`, `!tool.isActive`) separately — those are
 * orthogonal to policy.
 *
 * Matrix per Q9:
 *   auto_approve     → enabled
 *   require_approval → enabled (a confirmation dialog gates the
 *                      actual probe; UI must NOT disable the
 *                      button as that was the original #258 bug)
 *   forbidden        → disabled (cannot be invoked)
 */
export function isRunTestEnabledForPolicy(policy: ToolPolicy): boolean {
  return policy !== 'forbidden';
}

/**
 * Decide whether clicking Run Test should pop the confirmation
 * dialog (Q9 — write-policy tools require explicit confirmation).
 */
export function requiresConfirmDialog(policy: ToolPolicy): boolean {
  return policy === 'require_approval';
}

/**
 * Decide whether the Run Test button is hard-blocked (forbidden
 * policy). The route's onClick branches on this to surface a
 * toast instead of opening the confirm dialog.
 */
export function isForbiddenPolicy(policy: ToolPolicy): boolean {
  return policy === 'forbidden';
}

/**
 * Build the per-policy button label. The "Confirm & run test"
 * label is part of the #258 fix copy — it tells the user the
 * click won't run the probe directly.
 */
export function runTestButtonLabel(policy: ToolPolicy, isPending: boolean): string {
  if (isPending) return 'Testing…';
  if (policy === 'require_approval') return 'Confirm & run test';
  return 'Run test';
}

/**
 * Build the per-policy `title` attribute (tooltip on hover).
 */
export function runTestButtonTooltip(policy: ToolPolicy): string {
  if (policy === 'forbidden') return 'Forbidden policy — testing disabled.';
  if (policy === 'require_approval') return 'Opens a confirmation dialog before the probe runs.';
  return 'Run the connectivity probe.';
}

/**
 * Stable cache key for the "clear stale result on transition" effect
 * in the route. The route's useEffect compares the current key with
 * the previously-recorded key; if they differ AND the previous key
 * was non-null, the stale `testResult` is cleared.
 *
 * #258 — Without this, an `OK 145ms` result from an earlier
 * Auto-policy probe could remain visible on the screen under a
 * Require-Approval policy state. The key change forces a clear on
 * policy transitions.
 */
export function toolPolicyTransitionKey(toolId: string | null | undefined, policy: string | null | undefined): string | null {
  if (!toolId) return null;
  return `${toolId}::${policy ?? ''}`;
}
